import { useRef, useState } from 'react';
import axios from 'axios';
import auth from '@client/components/auth';
import { getCreatorApiUrl } from '@client/components/api-urls';
import { navigate, PATHS } from '@client/routes';

type UploadSummary = {
  message: string;
  errors?: string[];
  program?: {
    id: number;
    name: string;
    weeks: number;
    workouts: number;
    exercises: number;
  };
};

export type UploadStep = 'No File' | 'Uploading' | 'Transferred';

export type UploadProgramModel = {
  step: UploadStep;
  setFile: (file: File) => void;
  upload: (dryRun: boolean) => void;
  reset: () => void;
  uploadSummary: UploadSummary;
};

export const useUploadProgramModel = (
  influencerId: number,
): UploadProgramModel => {
  const [step, updateStep] = useState<UploadStep>('No File');
  const [uploadSummary, setUploadSummary] = useState<UploadSummary>(null);

  const fileRef = useRef<File>(null);

  const setFile = (file: File) => {
    fileRef.current = file;
  };

  const reset = () => {
    updateStep('No File');
    setUploadSummary(null);
  };

  const upload = async (dryRun = true) => {
    try {
      updateStep('Uploading');

      const formData = new FormData();
      formData.append('programData', fileRef.current);
      const { data: uploadSummary } = await axios.post<UploadSummary>(
        getCreatorApiUrl(
          `/experimental/program-upload?creatorId=${influencerId}&dryRun=${String(
            dryRun,
          )}`,
        ),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: auth.getToken(),
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total ?? 1),
            );
            console.log(`Upload progress: ${percent}%`);
          },
        },
      );

      if (!dryRun) {
        reset();
        return navigate(
          PATHS.CATEGORY_EDIT(uploadSummary.program.id, influencerId),
        );
      }

      setUploadSummary(uploadSummary);
      updateStep('Transferred');
    } catch (err) {
      const error = err as {
        code?: string;
        response: { data: UploadSummary };
      };
      if (error.code === 'ERR_BAD_REQUEST') {
        const { errors, message } = error.response.data;
        setUploadSummary({
          message,
          errors: errors || [],
        });
        updateStep('Transferred');
        return;
      }

      reset();
      console.error('Upload failed:', err);
    }
  };

  return {
    step,
    setFile,
    uploadSummary,
    upload,
    reset,
  };
};
